import type { ParsedPdf } from "./parseZipPdfs";

/** Set to true to enable console logs for literature reference validation. */
const DEBUG_LITERATURE_VALIDATION = false;

/** Normalize a reference id for matching: lowercase, strip .pdf, spaces to hyphens */
export function normalizeRefId(id: string): string {
  return id
    .toLowerCase()
    .replace(/\.pdf$/i, "")
    .trim()
    .replace(/\s+/g, "-");
}

/** One reference line: display name (id or derived) + full citation text */
export type ReferenceItem = {
  refName: string;
  citation: string;
};

/** Per-overview validation result */
export type OverviewValidation = {
  fileName: string;
  ectdKey: string;
  sectionFound: boolean;
  /** Full citation text for each numbered reference (e.g. 25 entries). */
  citedCitations: string[];
  /** Reference ids that matched a file in M4.3 / M5.4 (kept for backward compat). */
  valid: string[];
  /** Matched references: refName is the id, citation is the full citation text. */
  validItems: ReferenceItem[];
  /** Citation texts that could not be matched (kept for backward compat). */
  missing: string[];
  /** Unmatched references: refName is derived from citation (first line or excerpt), citation is full text. */
  missingItems: ReferenceItem[];
};

export type LiteratureValidationResult = {
  nonclinical: OverviewValidation | null;
  clinical: OverviewValidation | null;
};

/** "Literature References" or "References" - prefer the longer phrase to avoid TOC false hits */
const LITERATURE_HEADING_PATTERN = /literature\s+references|references/gi;

/**
 * True if the text immediately after the heading match looks like a TOC line:
 * only dots/spaces and a page number (e.g. " ......... 39" or " ................ 42").
 */
function looksLikeTocEntry(text: string, matchEndIndex: number): boolean {
  const nextNewline = text.indexOf("\n", matchEndIndex);
  const restOfLine = text.slice(
    matchEndIndex,
    nextNewline === -1 ? text.length : nextNewline
  );
  return /^[\s.]*\d+\s*$/.test(restOfLine.trimStart());
}

/**
 * Find the first occurrence of the section heading that is NOT a Table of Contents entry.
 * TOC lines look like "Literature References ............... 39" (heading + dots + page number).
 */
function findSectionStart(text: string): number {
  const re = new RegExp(LITERATURE_HEADING_PATTERN.source, "gi");
  const matches: Array<{ index: number; length: number; heading: string }> = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    matches.push({
      index: m.index,
      length: m[0].length,
      heading: m[0],
    });
  }
  const found = matches.find(
    (match) => !looksLikeTocEntry(text, match.index + match.length)
  );
  if (found) {
    if (DEBUG_LITERATURE_VALIDATION) {
      console.log(
        "[literature] extractLiteratureReferencesSection: using non-TOC match at",
        found.index,
        "heading",
        found.heading
      );
    }
    return found.index;
  }
  return -1;
}

/** Intro/header phrases that indicate a segment is not an actual reference entry */
const NOT_A_REFERENCE = /^(?:Literature\s+References|References)(?:\s|$)/i;
const INTRO_PATTERN =
  /references\s+are\s+included\s+in|the\s+following\s+references?|see\s+module\s+\d/i;

/** Footer line: document name + page X/Y (e.g. "Tigecycline 50 mg, powder for solution for infusion 39/42") */
const FOOTER_LINE = /\d+\/\d+\s*$/;

/**
 * Remove trailing lines that look like PDF footer (line ending with page X/Y, e.g. "39/42").
 */
function stripFooterLines(text: string): string {
  const lines = text.split(/\r?\n/);
  let end = lines.length;
  while (end > 0 && FOOTER_LINE.test(lines[end - 1]?.trim() ?? "")) {
    end -= 1;
  }
  return lines.slice(0, end).join("\n").trim();
}

/** True if the whole string looks like a single footer line (document title + page X/Y). */
function isOnlyFooterLine(text: string): boolean {
  const t = text.trim();
  return t.length > 0 && t.length < 200 && FOOTER_LINE.test(t);
}

/**
 * Split the Literature References section into individual numbered entries (1., 2., …).
 * Returns an array of full citation strings (one per reference).
 * Skips header text and intro lines (e.g. "Literature References\nReferences are included in Module 5:").
 * Only splits when the number is followed by an uppercase letter (start of author/title),
 * so page numbers like "1002. doi" in the middle of a citation are not treated as new entries.
 */
export function extractReferenceEntries(sectionText: string): string[] {
  if (!sectionText || !sectionText.trim()) return [];

  // Split only when "N. " is followed by uppercase (real list item), not e.g. "1002. doi"
  const segments = sectionText.split(/\n(?=\d{1,3}\.\s+[A-Z])/);
  return segments.reduce<string[]>((entries, seg) => {
    const trimmed = seg.trim();
    if (!trimmed) return entries;
    // Only process segments that start with "N. " (numbered list item); skip header/intro block
    if (!/^\d+\.\s/.test(trimmed)) return entries;
    // Strip leading "N. " from the segment to get the citation text
    let citation = trimmed.replace(/^\d+\.\s*/, "").trim();
    if (!citation) return entries;
    // Skip if citation is just the section header or short intro text
    const firstLine = citation.split(/\r?\n/)[0] ?? citation;
    if (
      NOT_A_REFERENCE.test(firstLine) &&
      (citation.length < 120 || INTRO_PATTERN.test(citation))
    )
      return entries;
    // Skip if this is only a PDF footer line (document name + page X/Y)
    if (isOnlyFooterLine(citation)) return entries;
    // Remove trailing footer lines (e.g. "Tigecycline 50 mg, powder for solution for infusion 39/42")
    citation = stripFooterLines(citation);
    if (!citation) return entries;
    entries.push(citation);
    return entries;
  }, []);
}

/**
 * Derive a short reference name from a citation for display (first 400 chars).
 */
export function refNameFromCitation(citation: string, maxLen = 400): string {
  const trimmed = citation.trim();
  if (!trimmed) return "(no title)";

  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen).trim()}…`;
}

/**
 * Try to match a citation string to an identifier in the valid set (e.g. barbour-2009).
 * Uses heuristics: valid id is like "author-year" or "keyword-keyword-year"; citation must
 * contain the name parts and the year.
 */
function matchCitationToValidId(
  citation: string,
  validSet: Set<string>
): string | null {
  const lower = citation.toLowerCase();
  const ids = Array.from(validSet);
  return (
    ids.find((id) => {
      const parts = id.split("-");
      const yearPart = parts.find((p) => /^\d{4}$/.test(p));
      const nameParts = yearPart ? parts.filter((p) => p !== yearPart) : parts;
      if (yearPart && !citation.includes(yearPart)) return false;
      return nameParts.every((part) => lower.includes(part));
    }) ?? null
  );
}

/**
 * Extract the "Literature References" (or "References") section from overview text.
 * Skips Table of Contents entries (heading followed by dots and page number).
 * Returns the substring from the first non-TOC heading until the next major heading or end.
 */
export function extractLiteratureReferencesSection(text: string): string {
  if (!text || !text.trim()) return "";

  const startIndex = findSectionStart(text);
  if (startIndex === -1) {
    if (DEBUG_LITERATURE_VALIDATION) {
      console.log(
        "[literature] extractLiteratureReferencesSection: no heading found"
      );
    }
    return "";
  }

  // From start of section, look for next major heading (all caps line or numbered section like "2.3.4")
  const afterSection = text.slice(startIndex);
  const nextHeading = afterSection.match(
    /\n\s*(?:[A-Z][A-Z0-9\s]{10,}|\d+\.\d+(?:\.\d+)*\s+[A-Z])/
  );
  const endIndex =
    nextHeading && typeof nextHeading.index === "number"
      ? startIndex + nextHeading.index
      : text.length;
  const section = text.slice(startIndex, endIndex);
  if (DEBUG_LITERATURE_VALIDATION) {
    console.log(
      "[literature] extractLiteratureReferencesSection: section length",
      section.length
    );
    console.log(
      "[literature] extractLiteratureReferencesSection: section",
      section
    );
  }
  return section;
}

/**
 * Extract reference entries from the section (numbered list 1., 2., …) and match each
 * to the valid set. Returns citation list plus valid/missing items, each with refName (id or derived) first.
 */
export function extractReferenceCandidates(
  sectionText: string,
  validSet: Set<string>
): {
  citedCitations: string[];
  valid: string[];
  validItems: ReferenceItem[];
  missing: string[];
  missingItems: ReferenceItem[];
} {
  const citedCitations = extractReferenceEntries(sectionText);
  const valid: string[] = [];
  const validItems: ReferenceItem[] = [];
  const missing: string[] = [];
  const missingItems: ReferenceItem[] = [];
  const matchedIds = new Set<string>();

  citedCitations.forEach((citation) => {
    const id = matchCitationToValidId(citation, validSet);
    if (id && !matchedIds.has(id)) {
      matchedIds.add(id);
      valid.push(id);
      validItems.push({ refName: id, citation });
    } else if (!id) {
      missing.push(citation);
      missingItems.push({
        refName: refNameFromCitation(citation),
        citation,
      });
    }
  });

  if (DEBUG_LITERATURE_VALIDATION) {
    console.log(
      "[literature] extractReferenceCandidates:",
      citedCitations.length,
      "citations,",
      valid.length,
      "valid,",
      missing.length,
      "missing"
    );
  }

  return {
    citedCitations,
    valid,
    validItems,
    missing,
    missingItems,
  };
}

function buildValidSet(
  parsed: ParsedPdf[],
  ectdKeyFilter: string
): Set<string> {
  const refs = parsed
    .filter((p) => p.ectdKey === ectdKeyFilter && p.ectdTitle)
    .map((p) => normalizeRefId(p.ectdTitle!));
  const set = new Set(refs);
  if (DEBUG_LITERATURE_VALIDATION) {
    console.log(
      "[literature] buildValidSet",
      ectdKeyFilter,
      ":",
      set.size,
      "refs",
      [...set].slice(0, 10),
      set.size > 10 ? "…" : ""
    );
  }
  return set;
}

function validateOneOverview(
  overview: ParsedPdf,
  validSet: Set<string>
): OverviewValidation {
  const sectionText = extractLiteratureReferencesSection(overview.text);
  const sectionFound = sectionText.length > 0;
  const { citedCitations, valid, validItems, missing, missingItems } =
    sectionFound
      ? extractReferenceCandidates(sectionText, validSet)
      : {
          citedCitations: [],
          valid: [],
          validItems: [],
          missing: [],
          missingItems: [],
        };

  if (DEBUG_LITERATURE_VALIDATION) {
    console.log(
      "[literature] validateOneOverview",
      overview.fileName,
      "| sectionFound:",
      sectionFound,
      "| citedCitations:",
      citedCitations.length,
      "| valid:",
      valid.length,
      "| missing:",
      missing.length
    );
  }

  return {
    fileName: overview.fileName,
    ectdKey: overview.ectdKey,
    sectionFound,
    citedCitations,
    valid,
    validItems,
    missing,
    missingItems,
  };
}

/**
 * Validate literature references: extract "Literature References" from m2-4 and m2-5
 * overview PDFs and check each cited ref exists in m4-3 (nonclinical) or m5-4 (clinical).
 */
export function validateLiteratureReferences(
  parsed: ParsedPdf[]
): LiteratureValidationResult {
  const validM4 = buildValidSet(parsed, "m4-3-literature-references");
  const validM5 = buildValidSet(parsed, "m5-4-literature-references");

  const nonclinicalOverview = parsed.find(
    (p) => p.ectdKey === "m2-4-nonclinical-overview"
  );
  const clinicalOverview = parsed.find(
    (p) => p.ectdKey === "m2-5-clinical-overview"
  );

  if (DEBUG_LITERATURE_VALIDATION) {
    console.log(
      "[literature] validateLiteratureReferences: validM4 size",
      validM4.size,
      "validM5 size",
      validM5.size
    );
    console.log(
      "[literature] overviews found: nonclinical",
      !!nonclinicalOverview,
      "clinical",
      !!clinicalOverview
    );
  }

  const result = {
    nonclinical: nonclinicalOverview
      ? validateOneOverview(nonclinicalOverview, validM4)
      : null,
    clinical: clinicalOverview
      ? validateOneOverview(clinicalOverview, validM5)
      : null,
  };

  if (DEBUG_LITERATURE_VALIDATION) {
    console.log("[literature] validateLiteratureReferences result:", result);
  }

  return result;
}
